<!--
 * 文件功能：应用根组件（动森风格整体框架）
 * 输入参数：无（接收路由渲染各页面）
 * 输出参数：无（渲染首屏加载遮罩、路由切换进度条、顶部导航栏、路由视图、页脚）
 * 返回值：无
 * 说明：Vue 3 <script setup> 版本。
 *       - 顶部为动森风格胶囊导航（HOME/SETTING/BLOG/CALENDAR/TOMATO）+ Time HUD 实时时钟；
 *       - 页脚保留 About / Quick Links / Contact 三栏信息，配 animal-island-vue 的海浪 Footer 装饰；
 *       - 保留原有登录状态检查逻辑（localStorage "honey-user" + 路由 meta.showNavbar）；
 *       - 全局反馈（动物森友会 UI）：
 *         1. 首屏加载：animal-island-vue 的 Loading 岛屿全屏遮罩，
 *            初始路由就绪后淡出；
 *         2. 路由切换：顶部固定 animal-island-vue 的 Progress 条纹进度条，
 *            beforeEach 启动（模拟推进至 80%），afterEach 完成（冲到 100% 后收起）。
 -->
<template>
  <Cursor :force-all="false">
    <div id="app">
      <!-- 首屏加载遮罩：动森岛屿 Loading（初始路由就绪后淡出） -->
      <div v-if="appLoading" class="ac-app-loading">
        <Loading :active="true" />
      </div>

      <!-- 路由切换进度条：动森条纹 Progress（固定在视口顶部） -->
      <div v-if="routeLoading" class="ac-route-progress">
        <Progress :percent="routeProgress" :show-info="false" size="small" :duration="0.15" />
      </div>

      <!-- 顶部导航栏：动森羊皮纸顶条 + 胶囊菜单 + Time HUD 时钟 -->
      <header v-if="shouldShowNavbar" class="ac-navbar">
        <div class="ac-navbar__inner">
          <router-link to="/" class="ac-navbar__brand">
            <span class="ac-navbar__leaf">🏝️</span>
            <span class="ac-navbar__title">Valinor</span>
          </router-link>

          <nav class="ac-nav">
            <router-link
              v-for="item in navItems"
              :key="item.path"
              :to="item.path"
              class="ac-nav__item"
              :class="{ 'is-active': route.path === item.path }"
            >
              <span class="ac-nav__icon">{{ item.icon }}</span>
              <span>{{ item.label }}</span>
            </router-link>
          </nav>

          <div class="ac-navbar__clock">
            <Time />
          </div>
        </div>
      </header>

      <!-- 主体内容 -->
      <main id="main-body" class="ac-main">
        <router-view />
      </main>

      <!-- 页脚：三栏信息卡 + 海浪装饰 -->
      <footer class="ac-footer">
        <Card class="ac-footer__card">
          <div class="ac-footer__grid">
            <div class="ac-footer__col">
              <h4 class="ac-footer__head">🏝 About Me</h4>
              <p class="ac-footer__text">
                Comrade Pancrase Dai is a communist who did not join the party, he loves to play
                basketball, usually the third and fourth positions, but his throwing skills are not
                good, he loves to watch some anime, he dreamed of being a writer when he was a
                child, and when he grew up, he was slow to react and liked to play games.
              </p>
            </div>
            <div class="ac-footer__col">
              <h4 class="ac-footer__head">🔗 Quick Links</h4>
              <div class="ac-footer__link">
                <a href="https://space.bilibili.com/32435693" target="_blank">📺 My Bilibili</a>
              </div>
              <div class="ac-footer__link">
                <a href="https://weibo.com/u/5316518027" target="_blank">💭 My Weibo</a>
              </div>
            </div>
            <div class="ac-footer__col">
              <h4 class="ac-footer__head">✉️ Contact Me</h4>
              <p class="ac-footer__text">📞 +86 13833988217</p>
              <p class="ac-footer__text">📱 +86 18920255540</p>
              <p class="ac-footer__text">📮 651370869@qq.com</p>
            </div>
          </div>
          <div class="ac-footer__copy">Copyright © 2023 PancraseDai's Personal Website. All Rights Reserved.</div>
          <div class="ac-footer__beian">
            备案号：<a href="https://beian.miit.gov.cn/" target="_blank">冀ICP备2024065561号-1</a>
          </div>
        </Card>
        <Footer type="sea" />
      </footer>

      <!-- 动森通知容器：全局挂载一次，各页面通过 Notification 命令式推送消息提示 -->
      <NotificationContainer />
    </div>
  </Cursor>
</template>

<script setup lang="ts">
import { ref, watch, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Card, Cursor, Footer, Loading, NotificationContainer, Progress, Time } from 'animal-island-vue'

const route = useRoute()
const router = useRouter()
const shouldShowNavbar = ref(false)

/** 首屏加载遮罩显隐（初始 true，路由就绪后延迟淡出以展示动森岛屿动画） */
const appLoading = ref(true)
/** 路由切换进度条显隐 */
const routeLoading = ref(false)
/** 路由切换进度条当前百分比（0-100） */
const routeProgress = ref(0)
/** 路由进度条模拟推进定时器句柄 */
let progressTimer: number | undefined

/** 导航菜单项配置 */
const navItems = [
  { path: '/', label: 'HOME', icon: '🏠' },
  { path: '/self_setting', label: 'SETTING', icon: '⚙️' },
  { path: '/blog', label: 'BLOG', icon: '📖' },
  { path: '/calendar', label: 'CALENDAR', icon: '📅' },
  { path: '/pomodoro', label: 'TOMATO', icon: '🍅' },
]

/**
 * 函数功能：根据登录状态与路由元信息控制导航栏显隐
 * 输入参数：无
 * 输出参数：无（更新 shouldShowNavbar 响应式变量）
 * 返回值：无
 */
function checkLoginStatus(): void {
  const loginStatusString = localStorage.getItem('honey-user')
  let isLoggedIn = false

  if (loginStatusString) {
    try {
      const userData = JSON.parse(loginStatusString)
      if (userData && Object.keys(userData).length > 0) {
        isLoggedIn = true
      }
    } catch (e) {
      isLoggedIn = false
    }
  }

  shouldShowNavbar.value = !!(isLoggedIn && route.meta.showNavbar)
}

// 路由变化时重新检查登录状态与导航栏显隐
watch(
  () => route.fullPath,
  () => checkLoginStatus(),
  { immediate: true }
)

// 修复：直接访问/刷新 "/" 时导航栏不显示——
// immediate 的 watch 在路由初始导航完成前执行，此时 route 还是 START_LOCATION
// （meta 为空对象），而初始导航完成后 fullPath 仍为 "/"（值未变化）不会再次触发
// watch；router.isReady() 在初始导航真正完成后 resolve，此时重查一次确保导航栏显示。
// 同时在初始路由就绪后关闭首屏 Loading 遮罩（保留短暂动画时间）
router.isReady().then(() => {
  checkLoginStatus()
  window.setTimeout(() => {
    appLoading.value = false
  }, 500)
})

// 路由跳转开始：显示顶部条纹进度条并模拟推进（懒加载页面时给出加载反馈）
router.beforeEach((to, from, next) => {
  if (to.path !== from.path) {
    routeLoading.value = true
    routeProgress.value = 8
    if (progressTimer !== undefined) window.clearInterval(progressTimer)
    progressTimer = window.setInterval(() => {
      // 模拟推进：上限 85%，完成时机交给 afterEach
      if (routeProgress.value < 85) {
        routeProgress.value = Math.min(85, routeProgress.value + 7)
      }
    }, 120)
  }
  next()
})

// 路由跳转完成：进度条冲到 100% 后收起
router.afterEach(() => {
  if (progressTimer !== undefined) {
    window.clearInterval(progressTimer)
    progressTimer = undefined
  }
  if (routeLoading.value) {
    routeProgress.value = 100
    window.setTimeout(() => {
      routeLoading.value = false
    }, 350)
  }
})

// 组件卸载时清理定时器
onBeforeUnmount(() => {
  if (progressTimer !== undefined) window.clearInterval(progressTimer)
})
</script>

<style>
/* ===== 首屏加载遮罩：动森岛屿 Loading 全屏居中 ===== */
.ac-app-loading {
  position: fixed;
  inset: 0;
  z-index: 3000;
}

/* ===== 路由切换进度条：动森条纹 Progress 固定在视口顶部 ===== */
.ac-route-progress {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2500;
  background: rgba(248, 248, 240, 0.55);
}

/* ===== 顶部导航栏（动森羊皮纸风格） ===== */
.ac-navbar {
  position: sticky;
  top: 0;
  z-index: 100;
  background: rgba(248, 248, 240, 0.92);
  backdrop-filter: blur(8px);
  border-bottom: 2px solid #c4b89e;
  box-shadow: 0 3px 10px 0 rgba(61, 52, 40, 0.1);
}

.ac-navbar__inner {
  max-width: 1600px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 10px 24px;
}

.ac-navbar__brand {
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  flex-shrink: 0;
}

.ac-navbar__leaf {
  font-size: 22px;
}

.ac-navbar__title {
  font-weight: 800;
  font-size: 18px;
  color: #794f27;
  letter-spacing: 0.02em;
}

.ac-nav {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  justify-content: center;
  flex-wrap: wrap;
}

/* 胶囊导航项 */
.ac-nav__item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 18px;
  border-radius: 50px;
  border: 2px solid #c4b89e;
  background: rgba(255, 255, 255, 0.6);
  color: #725d42;
  font-weight: 700;
  font-size: 14px;
  letter-spacing: 0.02em;
  text-decoration: none;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.ac-nav__item:hover {
  color: #19c8b9;
  border-color: #19c8b9;
  transform: translateY(-1px);
  box-shadow: 0 3px 10px 0 rgba(61, 52, 40, 0.1);
}

.ac-nav__item.is-active {
  background: #19c8b9;
  border-color: #19c8b9;
  color: #fff;
  box-shadow: 0 3px 0 0 #11a89b;
  transform: translateY(-1px);
}

.ac-navbar__clock {
  flex-shrink: 0;
}

/* ===== 主体区域（固定 1800px 宽，小屏自适应；宽度加大消除与内部卡片边框的重叠拥挤） ===== */
.ac-main {
  padding: 20px;
  min-height: 74vh;
  max-width: 1800px;
  margin: 0 auto;
  box-sizing: border-box;
  width: 100%;
}

/* ===== 页脚 ===== */
.ac-footer {
  margin-top: 40px;
}

.ac-footer__card {
  max-width: 1800px;
  margin: 0 auto 24px;
}

.ac-footer__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 24px;
  text-align: left;
}

.ac-footer__head {
  font-size: 20px;
  font-weight: 800;
  color: #794f27;
  margin: 0 0 12px;
}

.ac-footer__text {
  color: #725d42;
  font-size: 14px;
  line-height: 1.7;
  margin: 0 0 8px;
}

.ac-footer__link {
  margin-bottom: 10px;
}

.ac-footer__link a {
  color: #19c8b9;
  font-weight: 700;
  text-decoration: none;
}

.ac-footer__link a:hover {
  color: #11a89b;
}

.ac-footer__copy {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 2px dashed #d9cdb2;
  color: #9f927d;
  font-size: 13px;
}

.ac-footer__beian {
  margin-top: 8px;
  color: #9f927d;
  font-size: 13px;
}

.ac-footer__beian a {
  color: #19c8b9;
  text-decoration: none;
}

/* 响应式：小屏隐藏时钟、缩小间距、导航可横向滚动 */
@media (max-width: 768px) {
  .ac-navbar__clock {
    display: none;
  }
  .ac-navbar__inner {
    gap: 12px;
    padding: 8px 12px;
  }
  .ac-navbar__title {
    display: none;
  }
  /* 导航项在超小屏横向滚动，避免换行挤压 */
  .ac-nav {
    overflow-x: auto;
    flex-wrap: nowrap;
    scrollbar-width: none; /* Firefox */
    -webkit-overflow-scrolling: touch;
  }
  .ac-nav::-webkit-scrollbar {
    display: none;
  }
  .ac-nav__item {
    flex-shrink: 0;
    padding: 6px 14px;
    font-size: 13px;
  }
  .ac-main {
    padding: 12px;
  }
}
</style>
